import { useCallback, useEffect, useMemo, useState } from 'react'
import { DataResponse } from '../providers/mutations'
import { useOverwriteSearchParams, useSearchParams } from 'shared/hooks'
import {
  atom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { debounce } from '@mui/material'
import { objectValueSort } from '../utility'
import { csvMap } from './listQueries'

type orderValues = 'asc' | 'desc'
type sortType = { field: string; order: orderValues }
type filterValue = string | number | boolean
type filterType = Record<string, filterValue>

interface FetchParams {
  perPage: number
  currentPage: number
  filters: filterType
  sort: sortType
}

type resourceKey = 'sort' | 'filter' | 'page' | 'perPage'

const initialSort: sortType = { field: 'id', order: 'asc' }
const initialFilter = {}
const initialPage = 0
const initialPerPage = 25

const totalAtom = atom(0)

// This provides a generic function that converts search param string to an object
// returning the initial value if the search param value does not exist or if
// there's an issue converting to an object
function useObjectConvert<T>(initialValue: T) {
  return useCallback(
    (s: string | null): T => {
      try {
        return JSON.parse(s ?? '')
      } catch {
        return initialValue
      }
    },
    [initialValue],
  )
}

// This provides a generic function that converts search param string to a number
// returning the initial value if the search param value does not exist or if
// there's an issue converting to a number
function useNumberConvert(initialValue: number) {
  return useCallback(
    (s: string | null) => {
      const num = Number(s ?? initialValue)
      return Number.isNaN(num) ? initialValue : num
    },
    [initialValue],
  )
}

function useListDefaults() {
  const setTotal = useSetAtom(totalAtom)
  const setSearchParams = useOverwriteSearchParams()
  return useCallback(() => {
    setSearchParams({
      sort: JSON.stringify({ field: 'id', order: 'asc' }),
    })
    setTotal(0)
  }, [setSearchParams, setTotal])
}

// wraps the work for converting and setting search params
function useSearchResource<T>(
  key: resourceKey,
  stringConvert: (s: string | null) => T,
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const stringVal = searchParams.get(key)
  return useMemo(() => {
    return {
      data: stringConvert(stringVal),
      onChange: (newVal: T) =>
        setSearchParams({
          [key]: JSON.stringify(newVal),
        }),
    }
  }, [key, stringVal, stringConvert, setSearchParams])
}

export function useListPage() {
  const convert = useNumberConvert(initialPage)
  const { data, onChange } = useSearchResource<number>('page', convert)
  return useMemo(() => {
    return { page: data, setPage: onChange }
  }, [data, onChange])
}

export function useListPerPage() {
  const convert = useNumberConvert(initialPerPage)
  const { data, onChange } = useSearchResource<number>('perPage', convert)
  return useMemo(() => {
    return { perPage: data, setPerPage: onChange }
  }, [data, onChange])
}

export function useListFilters() {
  const convert = useObjectConvert<filterType>(initialFilter)
  const { data, onChange } = useSearchResource<filterType>('filter', convert)
  return useMemo(() => {
    const setFilter = (field: string, value: filterValue | undefined) => {
      if (value === undefined) {
        onChange(
          Object.fromEntries(
            Object.entries(data).filter(([key]) => key !== field),
          ),
        )
      } else {
        onChange({ ...data, [field]: value })
      }
    }
    return { filters: data, setFilter }
  }, [data, onChange])
}

export function useListSort() {
  const convert = useObjectConvert<sortType>(initialSort)
  const { data, onChange } = useSearchResource<sortType>('sort', convert)
  return useMemo(() => {
    const sortToggle = (newField: string) => {
      const newSort: sortType = data
      if (newField !== newSort.field) {
        newSort.field = newField
        newSort.order = 'asc'
      } else {
        newSort.order = newSort.order === 'asc' ? 'desc' : 'asc'
      }
      onChange(newSort)
    }
    return { ...data, sortToggle }
  }, [data, onChange])
}

export function useListPagination() {
  const { page, setPage } = useListPage()
  const { perPage, setPerPage } = useListPerPage()
  const total = useAtomValue(totalAtom)

  return useMemo(() => {
    return {
      page,
      onPageChange: setPage,
      rowsPerPage: perPage,
      onRowsPerPageChange: setPerPage,
      total,
    }
  }, [page, setPage, perPage, setPerPage, total])
}

// useListResource requires just a fetchFunc to provide what's needed for a paginated ResourceList
// onChangeFunc is called whenever data is fetched, this allows for selected item state to be reset
export function useListResource<T>(
  fetchFunc: (params: FetchParams) => Promise<DataResponse<T>>,
  onChangeFunc?: () => void,
) {
  const { page, rowsPerPage: perPage } = useListPagination()
  const { field, order } = useListSort()
  const { filters } = useListFilters()
  const [data, setData] = useState<T[]>([])
  const setTotal = useSetAtom(totalAtom)
  const reset = useListDefaults()

  const debounced = useCallback(
    debounce(async (params: FetchParams) => {
      const resp = await fetchFunc(params).catch(reset)
      setData(resp?.data ?? [])
      setTotal(resp?.total_count ?? 0)
    }, 800),
    [fetchFunc, reset],
  )

  const doFetch = useCallback(async () => {
    await debounced({
      perPage,
      currentPage: page + 1,
      filters,
      sort: { field, order },
    })
    onChangeFunc?.()
  }, [debounced, setTotal, perPage, page, field, order, filters, onChangeFunc])

  useEffect(() => {
    doFetch().then()
  }, [doFetch])

  return useMemo(() => {
    return {
      data,
      reFetch: doFetch,
    }
  }, [data, doFetch])
}

// Returns sorted list depending on the values returned from useListSort
// valueMap can contain functions that should be used to retrieve field values
// instead of accessing the fields directly off the items to be sorted
export function useSortableList<T>(
  list: T[],
  options: {
    valueMap?: Record<string, (item: T) => string | number>
  } = {},
) {
  const { order, field } = useListSort()
  return useMemo(() => {
    const { valueMap } = options
    const byFieldFunc = (item: T) => item[field as keyof T]
    const valFunc = valueMap?.[field] ?? byFieldFunc
    const sorted = [...list].sort(objectValueSort(valFunc))
    if (order === 'desc') {
      sorted.reverse()
    }
    return sorted
  }, [list, field, order, options])
}

export function useCSVDownload(resourceKey: string) {
  const { filters } = useListFilters()
  // check if resource is able to download csvs
  if (!(resourceKey in csvMap)) {
    return
  }
  const csvFunc = csvMap[resourceKey]
  return () => csvFunc(filters)
}
